import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TrackingSearch from './components/TrackingSearch';
import OrderTimeline from './components/OrderTimeline';
import OrderDetails from './components/OrderDetails';
import { mockOrders } from '../../mocks/trackingOrders';
import SEOHead from '../../components/feature/SEOHead';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://helixring.com';

const trackingSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Seguimiento de Pedido | HELIX Ring',
    description:
      'Rastrea el estado de tu pedido HELIX Ring en tiempo real. Introduce tu número de pedido para ver la ubicación y el estado de tu envío.',
    url: `${SITE_URL}/seguimiento`,
    inLanguage: 'es',
    isPartOf: {
      '@type': 'WebSite',
      name: 'HELIX Ring',
      url: SITE_URL,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Inicio',
          item: SITE_URL,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Seguimiento de Pedido',
          item: `${SITE_URL}/seguimiento`,
        },
      ],
    },
  },
];

type SearchState = 'idle' | 'loading' | 'found' | 'not_found';

const TrackingPage = () => {
  const [state, setState] = useState<SearchState>('idle');
  const [orderId, setOrderId] = useState('');
  const [foundOrder, setFoundOrder] = useState<(typeof mockOrders)[string] | null>(null);

  const handleSearch = (id: string) => {
    setOrderId(id);
    setState('loading');

    setTimeout(() => {
      const order = mockOrders[id];
      if (order) {
        setFoundOrder(order);
        setState('found');
      } else {
        setFoundOrder(null);
        setState('not_found');
      }
    }, 900);
  };

  const handleReset = () => {
    setState('idle');
    setFoundOrder(null);
    setOrderId('');
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <SEOHead schemas={trackingSchemas} />
      <Navbar />

      {/* Hero header */}
      <div className="relative pt-32 pb-16 bg-stone-950 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://public.readdy.ai/ai/img_res/cc755404d9b77a08ae142d9b8571ec90.jpg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-stone-950/80 to-stone-950" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-700/20 border border-amber-700/30 rounded-full px-4 py-1.5 mb-6">
            <div className="w-4 h-4 flex items-center justify-center text-amber-400">
              <i className="ri-truck-line text-sm" />
            </div>
            <span className="text-xs text-amber-400 font-body tracking-widest uppercase font-medium">
              Seguimiento de envío
            </span>
          </div>
          <h1 className="font-display text-3xl lg:text-5xl font-semibold text-white mb-4 tracking-tight">
            ¿Dónde está tu HELIX Ring?
          </h1>
          <p className="font-body text-stone-400 text-base lg:text-lg max-w-xl mx-auto leading-relaxed">
            Introduce tu número de pedido para ver el estado de tu envío en tiempo real.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="-mt-6 z-10 px-6 max-w-4xl mx-auto w-full">
        <TrackingSearch
          onSearch={handleSearch}
          loading={state === 'loading'}
          initialValue={orderId}
        />
      </div>

      {/* Results area */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-12">
        {/* Idle state — demo hint */}
        {state === 'idle' && (
          <div className="mt-4">
            <div className="bg-white border border-stone-100 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-amber-50 rounded-2xl">
                <i className="ri-map-pin-2-line text-2xl text-amber-700" />
              </div>
              <h3 className="font-display text-lg font-semibold text-stone-900 mb-2">
                Rastrea tu pedido al instante
              </h3>
              <p className="font-body text-sm text-stone-500 max-w-xs mx-auto leading-relaxed mb-6">
                Introduce el número de pedido del email de confirmación que recibiste al comprar.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <p className="text-xs text-stone-400 font-body w-full mb-1">Pedidos de ejemplo:</p>
                {Object.keys(mockOrders).map((key) => (
                  <button
                    key={key}
                    onClick={() => handleSearch(key)}
                    className="whitespace-nowrap text-xs font-body bg-stone-50 hover:bg-amber-50 border border-stone-200 hover:border-amber-200 text-stone-600 hover:text-amber-700 px-3 py-1.5 rounded-full transition-all duration-200 cursor-pointer"
                  >
                    {key}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Loading */}
        {state === 'loading' && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-loader-4-line text-2xl text-amber-700 animate-spin" />
            </div>
            <p className="font-body text-sm text-stone-500">Buscando pedido <strong className="text-stone-800">{orderId}</strong>…</p>
          </div>
        )}

        {/* Not found */}
        {state === 'not_found' && (
          <div className="bg-white border border-stone-100 rounded-2xl p-10 text-center mt-4">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-red-50 rounded-2xl">
              <i className="ri-search-eye-line text-2xl text-red-400" />
            </div>
            <h3 className="font-display text-lg font-semibold text-stone-900 mb-2">
              Pedido no encontrado
            </h3>
            <p className="font-body text-sm text-stone-500 max-w-xs mx-auto leading-relaxed mb-6">
              No encontramos el pedido <strong className="text-stone-700">&ldquo;{orderId}&rdquo;</strong>. Verifica que el número sea correcto y que coincida con el del email de confirmación.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleReset}
                className="whitespace-nowrap text-sm font-body bg-stone-100 hover:bg-stone-200 text-stone-700 px-5 py-2.5 rounded-full transition-all cursor-pointer"
              >
                Intentar de nuevo
              </button>
              <a
                href="mailto:hola@helixring.com"
                className="whitespace-nowrap text-sm font-body bg-amber-700 hover:bg-amber-800 text-white px-5 py-2.5 rounded-full transition-all cursor-pointer"
                rel="nofollow"
              >
                Contactar soporte
              </a>
            </div>
          </div>
        )}

        {/* Found */}
        {state === 'found' && foundOrder && (
          <div className="flex flex-col gap-5 mt-4">
            <div className="flex items-center justify-between">
              <p className="font-body text-sm text-stone-500">
                Mostrando resultados para <strong className="text-stone-800">{orderId}</strong>
              </p>
              <button
                onClick={handleReset}
                className="whitespace-nowrap text-xs font-body text-stone-400 hover:text-amber-700 flex items-center gap-1 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-line text-xs" />
                Nueva búsqueda
              </button>
            </div>
            <OrderTimeline order={foundOrder} />
            <OrderDetails order={foundOrder} />
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default TrackingPage;
